import { action, atom } from '@reatom/framework'
import BigNumber from 'bignumber.js'
import i18next from 'i18next'

import { AsyncStateN, Data, Result } from '@blade/std-lib'
import { CurrencyModel } from '@root/entities/currency'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { WrongCredentialsError } from '@root/features/auth'
import { requestLocalPasswordAction } from '@root/features/secure-storage/atoms'
import { reatomTransfer } from '@root/features/wallet/transfer'
import { CancelError } from '@root/shared'
import { showSnackbarAction } from '@root/shared/ui-kit'

import { transferAmountReatom } from './transfer-amount-reatom'
import { transferCurrencyReatom } from './transfer-currency-reatom'
import { transferDestinationReatom } from './transfer-destination-reatom'
import { transferGasSettingsReatom } from './transfer-fee-reatom'
import { reatomTransferSvmFee } from './transfer-svm-fee-reatom'

import type { WalletModel } from '@root/entities/wallet'

export class InsufficientBalanceError extends Data.TaggedError(
  'InsufficientBalance',
) {}
export class GasInfoLoadingError extends Data.TaggedError('GasInfoLoading') {}
export class NoDestinationError extends Data.TaggedError('NoDestination') {}

export type ValidationError =
  | InsufficientBalanceError
  | GasInfoLoadingError
  | NoDestinationError

export class InputTransferStep extends Data.TaggedClass('Input') {}
export class ConfirmTransferStep extends Data.TaggedClass('Confirm') {}

export type TransferStep = InputTransferStep | ConfirmTransferStep

export const reatomTransferFlow = ({ wallet }: { wallet: WalletModel }) => {
  const currencyModel = transferCurrencyReatom(wallet)
  const destinationModel = transferDestinationReatom({
    currencyAtom: currencyModel.currencyAtom,
  })
  const amountModel = transferAmountReatom({
    currencyAtom: currencyModel.currencyAtom,
    wallet: wallet,
  })
  const evmFeeModel = transferGasSettingsReatom()
  // eslint-disable-next-line @reatom/unit-naming-rule
  const svmFeeModel = reatomTransferSvmFee()

  const stepAtom = atom<TransferStep>(new InputTransferStep(), 'stepAtom')

  // eslint-disable-next-line @reatom/unit-naming-rule
  const transferStateAtom = reatomTransfer()

  const validationAtom = atom((ctx): ValidationError | undefined => {
    const destination = ctx.spy(destinationModel.addressAtom)
    if (!destination) {
      return new NoDestinationError()
    }
    const network = ctx.spy(selectedNetworkAtom)
    const amount = ctx.spy(amountModel.amountAtom)
    const balance = ctx.spy(amountModel.balanceAtom)
    if (balance.lt(amount)) {
      return new InsufficientBalanceError()
    }

    if (network === Network.Ethereum) {
      const gasState = ctx.spy(evmFeeModel.gasInformationStateAtom)
      if (!AsyncStateN.isSuccess(gasState)) {
        return new GasInfoLoadingError()
      }
    }
    return undefined
  }, 'validationAtom')

  const initializeAction = action((_ctx) => {}, 'initializeAction')

  const executeTransferAction = action(async (ctx) => {
    const network = ctx.get(selectedNetworkAtom)
    const destination = ctx.get(destinationModel.addressAtom)
    const amount = ctx.get(amountModel.amountAtom)
    const currency = ctx.get(currencyModel.currencyAtom)
    const evmGasSettings = ctx.get(evmFeeModel.selectedGasSettings)
    const svmGasSettings = ctx.get(svmFeeModel.svmFeeSettingsAtom)

    const evmGasParams = (() => {
      if (evmGasSettings.type === 'Recommended') {
        if (!evmGasSettings.settings.value) {
          return
        }
        const { suggestedMaxFeePerGas, suggestedMaxPriorityFeePerGas } =
          evmGasSettings.settings.value

        return {
          baseFee: suggestedMaxFeePerGas,
          priorityFee: suggestedMaxPriorityFeePerGas,
        }
      }

      const { suggestedMaxFeePerGas, suggestedMaxPriorityFeePerGas } =
        evmGasSettings.settings

      return {
        baseFee: suggestedMaxFeePerGas,
        priorityFee: suggestedMaxPriorityFeePerGas,
      }
    })()

    if (network === Network.Ethereum && !evmGasParams) {
      return
    }

    const result = await ctx.schedule(async () =>
      requestLocalPasswordAction(ctx, async (ctx, { password }) => {
        const result = await ctx.schedule(() =>
          transferStateAtom.execute(ctx, {
            address: destination,
            amount: amount,
            currency: currency,
            maxBaseFeeGwei: evmGasParams?.baseFee ?? 0,
            priorityFeeGwei: evmGasParams?.priorityFee ?? 0,
            svm: svmGasSettings,
            walletId: wallet.id,
            password: password,
          }),
        )

        if (Result.isFailure(result)) {
          if (result.failure instanceof WrongCredentialsError) {
            return result
          }
          showSnackbarAction(ctx, {
            text: i18next.t('common.something-went-wrong'),
            intent: 'error',
          })
        }

        return result
      }),
    )

    if (result instanceof CancelError) {
      transferStateAtom.reset(ctx)
    }
  }, 'executeTransferAction')

  const totalTransferAmountAtom = atom((ctx) => {
    const amount = ctx.spy(amountModel.amountAtom)
    const currency = ctx.spy(currencyModel.currencyAtom)

    if (CurrencyModel.isEthOrWeth(currency)) {
      const estimatedFeeState = ctx.spy(
        evmFeeModel.estimatedTransferGasFeeEthStateAtom,
      )
      if (!AsyncStateN.isSuccess(estimatedFeeState)) {
        return undefined
      }

      return amount.plus(estimatedFeeState.value)
    }

    if (CurrencyModel.isSolOrWsol(currency)) {
      const fee = ctx.spy(svmFeeModel.totalFeeAtom)
      return amount.plus(fee)
    }

    return amount
  }, 'totalTransferAmountAtom')

  const totalTransferAmountUsdAtom = atom((ctx) => {
    const amount = ctx.spy(totalTransferAmountAtom)
    const usePrice = ctx.spy(amountModel.currencyPriceUsdStateAtom).value
    if (!amount || !usePrice) {
      return undefined
    }
    return amount.multipliedBy(usePrice)
  }, 'totalTransferAmountUsdAtom')

  const maxTotalTransferAmountAtom = atom((ctx) => {
    const amount = ctx.spy(amountModel.amountAtom)
    const currency = ctx.spy(currencyModel.currencyAtom)

    if (CurrencyModel.isEthOrWeth(currency)) {
      const estimatedFeeState = ctx.spy(
        evmFeeModel.estimatedTransferMaxGasFeeEthStateAtom,
      )
      if (!AsyncStateN.isSuccess(estimatedFeeState)) {
        return undefined
      }

      return amount.plus(estimatedFeeState.value)
    }

    return amount
  }, 'maxTotalTransferAmountAtom')

  const estimatedBalanceAfterStateAtom = atom((ctx) => {
    const balance = ctx.spy(amountModel.balanceAtom)
    const totalAmount = ctx.spy(totalTransferAmountAtom) ?? BigNumber(0)

    return balance.minus(totalAmount)
  }, 'estimatedBalanceAfterStateAtom')

  const estimatedBalanceAfterUsdAtom = atom((ctx) => {
    const balance = ctx.spy(estimatedBalanceAfterStateAtom)
    const usePrice = ctx.spy(amountModel.currencyPriceUsdStateAtom).value

    if (!usePrice) {
      return undefined
    }

    return balance.multipliedBy(usePrice)
  }, 'estimatedBalanceAfterUsdAtom')

  return {
    validationAtom,
    initializeAction,
    wallet,
    stepAtom,
    transferStateAtom,
    executeTransferAction,
    estimatedBalanceAfterStateAtom,
    estimatedBalanceAfterUsdAtom,
    totalTransferAmountAtom,
    totalTransferAmountUsdAtom,
    maxTotalTransferAmountAtom,
    amount: amountModel,
    currency: currencyModel,
    destination: destinationModel,
    gasSettings: evmFeeModel,
    svmFeeModel,
  }
}
export type TransferFlowReatom = ReturnType<typeof reatomTransferFlow>
